import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import {PublishOutlined} from '@material-ui/icons';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import FileSelector from '../../../shared/fileselector/FileSelector.js';
import Message from '../../../shared/message/Message.js';
import BlockUI from '../../../shared/blockui/BlockUI.tsx';
import useCreate from '../../../../hooks/useCreate.js';
import useErrorMessage from '../../../../hooks/useErrorMessage.js';
import {arquivoParaBase64} from '../../../../utils/arquivo.js';
import {mensagemDoErro} from '../../../../utils/erro.js';
import {URL_PROCESSOS_SELECAO_IMPORTAR_CARGAS} from '../../../../constants/index.js';

const FORMATOS_XLSX = [{format: 'XLSX', mimeType: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']}];
const TAMANHO_MAXIMO = 10 * 1024 * 1024;
const ERRO_PADRAO = 'Não foi possível ler a planilha. Confira o layout e tente novamente.';

const linhas = textos => textos.map(texto => String(texto ?? '').trim()).filter(Boolean);

/* Mensagens do 400 da importação: texto (uma por linha), lista ou objeto com `mensagens`/`mensagem`. */
const mensagensDoErro = erro => {
    if (Array.isArray(erro)) return linhas(erro);
    if (erro && Array.isArray(erro.mensagens)) return linhas(erro.mensagens);
    return linhas(mensagemDoErro(erro, ERRO_PADRAO).split(/\r?\n/));
};

const useStyles = makeStyles(theme => ({
    paper: {
        width: 800,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    },
    paragrafo: {
        marginBottom: theme.spacing(2)
    },
    listaErros: {
        margin: 0,
        paddingLeft: theme.spacing(2.5)
    }
}));

/*
 * Importação da carga de candidatos por planilha (_formImportarCarga do
 * PrimeFaces): xlsx de até 10 MB, enviado em base64 ao back, que devolve os
 * candidatos lidos ou, com erro (400), as mensagens por linha — mostradas aqui
 * mesmo. Quem acrescenta à lista (e avisa os CPFs repetidos) é a aba Carga.
 */
export default function DialogImportarCarga({open, onClose, onImportar}) {
    const classes = useStyles();
    const [create, enviando] = useCreate({returnInnerLoadingState: true});
    const showErrorMessage = useErrorMessage();
    const [arquivo, setArquivo] = useState(null);
    const [erros, setErros] = useState([]);

    const aoAbrir = () => {
        setArquivo(null);
        setErros([]);
    };

    const selecionar = selecionado => {
        setArquivo(selecionado);
        setErros([]);
    };

    const importar = async () => {
        let conteudo;
        try {
            conteudo = await arquivoParaBase64(arquivo);
        } catch (e) {
            showErrorMessage('Não foi possível ler o arquivo selecionado. Tente escolhê-lo novamente.');
            return;
        }
        create(URL_PROCESSOS_SELECAO_IMPORTAR_CARGAS, {nomeArquivo: arquivo.name, conteudo}, resposta => {
            onImportar(Array.isArray(resposta?.cargas) ? resposta.cargas : []);
            onClose();
        }, erro => {
            // Erro de leitura/validação da planilha: as mensagens (uma por linha) ficam no diálogo.
            const mensagens = mensagensDoErro(erro);
            setErros(mensagens.length ? mensagens : [ERRO_PADRAO]);
        });
    };

    return (
        <Dialog open={open} onClose={onClose} TransitionProps={{onEntering: aoAbrir}} scroll='body'
                aria-labelledby='dialog-importar-carga-titulo' classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-importar-carga-titulo'>Importar carga (xlsx)</DialogTitle>
            <DialogContent classes={{root: classes.contentRoot}}>
                <BlockUI loading={enviando}>
                    <Typography variant='body2' className={classes.paragrafo}>
                        Layout esperado (primeira linha = cabeçalho): <b>A</b> Nome | <b>B</b> CPF | <b>C</b> Cód. Inscrito
                        | <b>D</b> Data Nascimento (dd/mm/aaaa).
                    </Typography>
                    {!!erros.length && (
                        <div className={classes.paragrafo}>
                            <Message type='error' variant='standard'>
                                <ul className={classes.listaErros}>
                                    {erros.map((linha, indice) => <li key={indice}>{linha}</li>)}
                                </ul>
                            </Message>
                        </div>
                    )}
                    <LabelValue label='Planilha (xlsx de até 10 MB) *'>
                        <FileSelector
                            htmlFor='planilhaCargaProcessoSelecao'
                            buttonText='Selecionar'
                            validFormats={FORMATOS_XLSX}
                            maxSize={TAMANHO_MAXIMO}
                            onFile={arquivo ? {nome: arquivo.name} : null}
                            onSelect={selecionar}
                            onRemove={() => selecionar(null)}/>
                    </LabelValue>
                </BlockUI>
            </DialogContent>
            <DialogActions>
                <TextButton onClick={onClose}>Fechar</TextButton>
                <ContainedButton startIcon={<PublishOutlined/>} disabled={!arquivo || enviando} onClick={importar}>
                    Importar
                </ContainedButton>
            </DialogActions>
        </Dialog>
    );
}

DialogImportarCarga.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /* Recebe os candidatos lidos da planilha. */
    onImportar: PropTypes.func.isRequired
};
